import { Injectable } from "@angular/core";
import { CosmeticOptions } from "../models/filter-models";
import { Color, MinimapIconSize, MinimapIconShape, LabelSize } from "../models/enums";
import { Actions } from "../models/parsing-models";

@Injectable({
  providedIn: 'root'
})
export class CosmeticsParserService {
  /**
   * Reverse-engineers CosmeticOptions from parsed filter actions.
   * This mirrors the logic in rule-generator.service.ts that constructs actions from cosmeticOptions.
   */
  parseCosmeticOptionsFromActions(actions: Actions): CosmeticOptions | undefined {
    const cosmeticOptions = new CosmeticOptions();

    // Parse PlayEffect: "Red" or "Red Temp"
    if (actions.playEffect) {
      const playEffectParts = actions.playEffect.trim().split(/\s+/);
      const colorName = playEffectParts[0];
      const temp = playEffectParts.includes('Temp');

      // Try to match color name to Color enum
      const colorValue = this.parseColorName(colorName);
      if (colorValue) {
        cosmeticOptions.highlight = true;
        cosmeticOptions.beam = true;
        cosmeticOptions.highlightColor = colorValue;
        cosmeticOptions.temp = temp;
      }
    }

    // Parse MinimapIcon: "1 White Circle"
    if (actions.minimapIcon) {
      const minimapIconParts = actions.minimapIcon.trim().split(/\s+/);
      if (minimapIconParts.length >= 3) {
        const sizeStr = minimapIconParts[0];
        const colorName = minimapIconParts[1];
        const shapeName = minimapIconParts[2];

        // Parse size: 0=Large, 1=Medium, 2=Small
        const sizeNum = parseInt(sizeStr, 10);
        if (!isNaN(sizeNum) && sizeNum >= 0 && sizeNum <= 2) {
          cosmeticOptions.minimapIconSize = sizeNum as MinimapIconSize;
        }

        // Parse color
        const colorValue = this.parseColorName(colorName);
        if (colorValue) {
          cosmeticOptions.highlight = true;
          cosmeticOptions.highlightColor = colorValue;
        }

        // Parse shape
        const shapeValue = this.parseMinimapIconShape(shapeName);
        if (shapeValue) {
          cosmeticOptions.minimapIconShape = shapeValue;
        }
      }
    }

    // Parse PlayAlertSound: "2 200"
    if (actions.playAlertSound) {
      const soundParts = actions.playAlertSound.trim().split(/\s+/);
      if (soundParts.length >= 1) {
        cosmeticOptions.alertSound = true;
        cosmeticOptions.alertSoundSound = soundParts[0];
        if (soundParts.length >= 2) {
          const volume = parseInt(soundParts[1], 10);
          if (!isNaN(volume)) {
            cosmeticOptions.alertSoundVolume = volume;
          }
        }
      }
    }

    // Parse CustomAlertSound: "\"sound.mp3\" 200"
    if (actions.customAlertSound) {
      const customSoundMatch = actions.customAlertSound.match(/"([^"]+)"/);
      if (customSoundMatch) {
        cosmeticOptions.alertSound = true;
        cosmeticOptions.customAlertSound = customSoundMatch[1];

        // Extract volume after the quoted string
        const volumeMatch = actions.customAlertSound.match(/"([^"]+)"\s+(\d+)/);
        if (volumeMatch && volumeMatch[2]) {
          const volume = parseInt(volumeMatch[2], 10);
          if (!isNaN(volume)) {
            cosmeticOptions.alertSoundVolume = volume;
          }
        }
      }
    }

    // Parse DisableDropSound: presence indicates true
    if (actions.disableDropSound) {
      cosmeticOptions.disableDropSound = true;
    }

    // Parse SetTextColor: "255 0 0 255" or color name
    if (actions.setTextColor) {
      const colorStr = this.parseColorValue(actions.setTextColor);
      if (colorStr) {
        cosmeticOptions.textColor = colorStr;
      }
    }

    // Parse SetBackgroundColor: "255 0 0 255" or color name
    if (actions.setBackgroundColor) {
      const colorStr = this.parseColorValue(actions.setBackgroundColor);
      if (colorStr) {
        cosmeticOptions.backgroundColor = colorStr;
      }
    }

    // Parse SetBorderColor: "255 0 0 255" or color name
    if (actions.setBorderColor) {
      const colorStr = this.parseColorValue(actions.setBorderColor);
      if (colorStr) {
        cosmeticOptions.borderColor = colorStr;
      }
    }

    // Parse SetFontSize: "33" -> LabelSize enum value
    if (actions.setFontSize) {
      const fontSizeNum = parseInt(actions.setFontSize.trim(), 10);
      if (!isNaN(fontSizeNum)) {
        // Map numeric values to LabelSize enum
        const labelSize = this.parseLabelSize(fontSizeNum);
        if (labelSize !== undefined) {
          cosmeticOptions.fontSize = labelSize;
        }
      }
    }

    // Only return cosmeticOptions if it has at least one property set
    if (Object.keys(cosmeticOptions).length > 0) {
      return cosmeticOptions;
    }

    return undefined;
  }

  /**
   * Parses a color name string to Color enum value.
   */
  private parseColorName(colorName: string): Color | undefined {
    const normalized = colorName.trim();
    // Check if it matches any Color enum value
    const colorValues = Object.values(Color);
    if (colorValues.includes(normalized as Color)) {
      return normalized as Color;
    }
    return undefined;
  }

  /**
   * Parses a minimap icon shape name to MinimapIconShape enum value.
   */
  private parseMinimapIconShape(shapeName: string): MinimapIconShape | undefined {
    const normalized = shapeName.trim();
    const shapeValues = Object.values(MinimapIconShape);
    if (shapeValues.includes(normalized as MinimapIconShape)) {
      return normalized as MinimapIconShape;
    }
    return undefined;
  }

  /**
   * Parses a color value from filter text (RGB format "255 0 0 255" or color name).
   * Returns a hex color string or rgb() format string.
   */
  private parseColorValue(colorValue: string): string | undefined {
    const trimmed = colorValue.trim();

    // Check if it's a color name first
    const colorName = this.parseColorName(trimmed);
    if (colorName) {
      // Convert color name to hex (approximate mapping)
      return this.colorNameToHex(colorName);
    }

    // Parse RGB format: "255 0 0 255" or "255 0 0"
    const rgbParts = trimmed.split(/\s+/).map(p => parseInt(p.trim(), 10));
    if (rgbParts.length >= 3 && rgbParts.every(p => !isNaN(p) && p >= 0 && p <= 255)) {
      const r = rgbParts[0];
      const g = rgbParts[1];
      const b = rgbParts[2];
      const a = rgbParts.length >= 4 ? rgbParts[3] : 255;

      if (a === 255) {
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      } else {
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${a.toString(16).padStart(2, '0')}`;
      }
    }

    return undefined;
  }

  /**
   * Converts a Color enum value to an approximate hex color.
   */
  private colorNameToHex(color: Color): string {
    const colorMap: { [key in Color]: string } = {
      [Color.Red]: '#FF0000',
      [Color.Green]: '#00FF00',
      [Color.Blue]: '#0000FF',
      [Color.Brown]: '#8B4513',
      [Color.White]: '#FFFFFF',
      [Color.Yellow]: '#FFFF00',
      [Color.Cyan]: '#00FFFF',
      [Color.Grey]: '#808080',
      [Color.Orange]: '#FFA500',
      [Color.Pink]: '#FFC0CB',
      [Color.Purple]: '#800080'
    };
    return colorMap[color] || '#FFFFFF';
  }

  /**
   * Parses a numeric font size to LabelSize enum value.
   */
  private parseLabelSize(fontSize: number): number | undefined {
    // Map numeric values to LabelSize enum values
    const labelSizeMap: { [key: number]: number } = {
      21: LabelSize.Smallest,
      25: LabelSize.Smaller,
      29: LabelSize.Small,
      33: LabelSize.Normal,
      37: LabelSize.Large,
      41: LabelSize.Larger,
      45: LabelSize.Largest
    };
    return labelSizeMap[fontSize];
  }
}

